// Personal lift-name overlay. Each user can rename a lift for themselves; the
// shared catalog (grit_exercises) is never touched. Names resolve at render time
// so renaming a lift also relabels all of that user's past history.
import { sb } from './db.js';
import { activeUser, viewing } from './auth.js';

// user -> Map(exercise_id -> custom name)
const caches = new Map();
function mapFor(user) { return caches.get(user) || new Map(); }

export async function loadExerciseNames(user = activeUser()) {
  if (!user) return new Map();
  const { data } = await sb.from('grit_exercise_names').select('exercise_id, name').eq('user_id', user);
  const m = new Map((data || []).map((r) => [r.exercise_id, r.name]));
  caches.set(user, m);
  return m;
}

// Load once per user (screens call this before rendering the viewed user's data).
export async function ensureNames(user = viewing()) {
  if (!caches.has(user)) await loadExerciseNames(user);
  return mapFor(user);
}

export function displayName(exercise, user = viewing()) {
  if (!exercise) return '';
  return mapFor(user).get(exercise.id) || exercise.name || '';
}

export function displayNameById(id, fallback = '', user = viewing()) {
  return mapFor(user).get(id) || fallback;
}

// Custom names are user-controlled and readable by every athlete (read-all RLS),
// so they MUST be HTML-escaped at every innerHTML render site. Use the *Html
// variants there; keep the raw ones for textContent, comparisons, and toasts.
export function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
export function displayNameHtml(exercise, user = viewing()) { return escapeHtml(displayName(exercise, user)); }
export function displayNameByIdHtml(id, fallback = '', user = viewing()) { return escapeHtml(displayNameById(id, fallback, user)); }

export async function setName(exerciseId, name) {
  const me = activeUser();
  const trimmed = (name || '').trim();
  if (!trimmed) return resetName(exerciseId);
  const { error } = await sb.from('grit_exercise_names')
    .upsert({ user_id: me, exercise_id: exerciseId, name: trimmed, updated_at: new Date().toISOString() }, { onConflict: 'user_id,exercise_id' });
  if (error) { console.warn('setName failed', error); return false; }
  if (!caches.has(me)) caches.set(me, new Map());
  caches.get(me).set(exerciseId, trimmed);
  return true;
}

export async function resetName(exerciseId) {
  const me = activeUser();
  const { error } = await sb.from('grit_exercise_names').delete().eq('user_id', me).eq('exercise_id', exerciseId);
  if (error) { console.warn('resetName failed', error); return false; }
  if (caches.has(me)) caches.get(me).delete(exerciseId);
  return true;
}

// test seam
export function __setCacheForTest(user, obj) { caches.set(user, new Map(Object.entries(obj))); }
